import React from 'react';

/**
 * BIS UI kit — zajednički moderni wireframe za SVE module (kraj "100 malih svemira").
 * Neutralna moderna baza (sive nijanse) + akcent preko var(--ad-acc) (gold/indigo po temi).
 * Gust, prozračan, SaaS-style. Moduli se povinuju ovome umjesto bespoke inline UX-a.
 */

export const ui = {
    ink: '#1f2433', ink2: '#5b6172', muted: '#8a90a3',
    line: '#e7e9f0', line2: '#eef0f6', bg: '#f6f7fb', card: '#fff',
    acc: 'var(--ad-acc)', accSoft: 'rgba(var(--ad-acc-rgb), 0.12)',
    radius: 14, radiusSm: 10,
} as const;

/* ── Page wrapper — standardni okvir modula ──────────────────────────────── */
export function Page({ children }: { children: React.ReactNode }) {
    return <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>{children}</div>;
}

/* ── Page header — naslov + podnaslov + akcije ───────────────────────────── */
export function PageHeader({ icon, title, subtitle, actions }: { icon?: React.ReactNode; title: string; subtitle?: string; actions?: React.ReactNode }) {
    return (
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 13, minWidth: 0 }}>
                {icon ? <div style={{ width: 44, height: 44, minWidth: 44, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.35rem', background: ui.accSoft, color: ui.acc }}>{icon}</div> : null}
                <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: '1.3rem', fontWeight: 800, color: ui.ink, lineHeight: 1.15 }}>{title}</div>
                    {subtitle ? <div style={{ fontSize: '0.85rem', color: ui.muted, marginTop: 2 }}>{subtitle}</div> : null}
                </div>
            </div>
            {actions ? <div style={{ display: 'flex', gap: 9, alignItems: 'center', flexWrap: 'wrap' }}>{actions}</div> : null}
        </div>
    );
}

/* ── Card ────────────────────────────────────────────────────────────────── */
export function Card({ children, pad = 0, style }: { children: React.ReactNode; pad?: number; style?: React.CSSProperties }) {
    return <div style={{ background: ui.card, border: `1px solid ${ui.line}`, borderRadius: ui.radius, boxShadow: '0 1px 3px rgba(20,22,50,0.04), 0 8px 24px rgba(20,22,50,0.04)', padding: pad, ...style }}>{children}</div>;
}

/* ── Button ──────────────────────────────────────────────────────────────── */
type BtnVariant = 'primary' | 'ghost' | 'soft' | 'danger';
export function Btn({ children, variant = 'ghost', onClick, disabled, type = 'button', size = 'md' }: { children: React.ReactNode; variant?: BtnVariant; onClick?: () => void; disabled?: boolean; type?: 'button' | 'submit'; size?: 'sm' | 'md' }) {
    const base: React.CSSProperties = {
        display: 'inline-flex', alignItems: 'center', gap: 7, cursor: disabled ? 'default' : 'pointer',
        fontWeight: 650, fontSize: size === 'sm' ? '0.78rem' : '0.85rem', padding: size === 'sm' ? '6px 11px' : '9px 15px',
        borderRadius: ui.radiusSm, border: '1px solid transparent', transition: 'filter 0.12s, background 0.12s', whiteSpace: 'nowrap', opacity: disabled ? 0.55 : 1,
    };
    const v: Record<BtnVariant, React.CSSProperties> = {
        primary: { background: ui.acc, color: '#fff', boxShadow: '0 4px 12px rgba(var(--ad-acc-rgb), 0.32)' },
        soft: { background: ui.accSoft, color: ui.acc },
        ghost: { background: '#fff', color: ui.ink2, borderColor: ui.line },
        danger: { background: '#fff', color: '#dc2626', borderColor: '#f2c9c9' },
    };
    return <button type={type} disabled={disabled} onClick={onClick} className="ui-btn" style={{ ...base, ...v[variant] }}>{children}</button>;
}

/* ── Inputi ──────────────────────────────────────────────────────────────── */
const fieldBase: React.CSSProperties = { height: 38, padding: '0 12px', borderRadius: ui.radiusSm, border: `1px solid ${ui.line}`, fontSize: '0.86rem', color: ui.ink, background: '#fff', outline: 'none' };
export function TextInput({ value, onChange, placeholder, icon, width }: { value: string; onChange: (v: string) => void; placeholder?: string; icon?: string; width?: number | string }) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: 38, padding: '0 12px', borderRadius: ui.radiusSm, border: `1px solid ${ui.line}`, background: '#fff', width: width ?? 260, minWidth: 0 }}>
            {icon ? <span style={{ color: ui.muted, fontSize: '0.9rem' }}>{icon}</span> : null}
            <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: '0.86rem', color: ui.ink, width: '100%', minWidth: 0 }} />
        </div>
    );
}
export function Select({ value, onChange, children, width }: { value: string; onChange: (v: string) => void; children: React.ReactNode; width?: number | string }) {
    return <select value={value} onChange={(e) => onChange(e.target.value)} style={{ ...fieldBase, width: width ?? 'auto', cursor: 'pointer' }}>{children}</select>;
}

/* ── Status pill ─────────────────────────────────────────────────────────── */
export function Pill({ children, tone = 'neutral' }: { children: React.ReactNode; tone?: 'ok' | 'warn' | 'bad' | 'info' | 'neutral' }) {
    const map = {
        ok: { bg: '#e7f6ec', fg: '#1a7a40' }, warn: { bg: '#fef3da', fg: '#9a6700' },
        bad: { bg: '#fde7ea', fg: '#c0264a' }, info: { bg: 'rgba(var(--ad-acc-rgb),0.12)', fg: 'var(--ad-acc)' },
        neutral: { bg: '#eef0f6', fg: '#5b6172' },
    }[tone];
    return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 999, fontSize: '0.74rem', fontWeight: 700, background: map.bg, color: map.fg }}>{children}</span>;
}

/* ── DataTable ───────────────────────────────────────────────────────────── */
export type Col<T> = { key: string; label: string; render?: (row: T) => React.ReactNode; width?: number | string; align?: 'left' | 'right' | 'center' };
export function DataTable<T extends { id: number | string }>({ cols, rows, onRowClick, loading, empty, rowKey }: {
    cols: Col<T>[]; rows: T[] | null; onRowClick?: (row: T) => void; loading?: boolean; empty?: string; rowKey?: (row: T) => string | number;
}) {
    return (
        <div style={{ overflowX: 'auto' }}>
            <table className="ui-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.86rem' }}>
                <thead>
                    <tr>
                        {cols.map((c) => (
                            <th key={c.key} style={{ position: 'sticky', top: 0, zIndex: 1, textAlign: c.align ?? 'left', padding: '11px 14px', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: ui.muted, background: '#fafbfd', borderBottom: `1px solid ${ui.line}`, width: c.width, whiteSpace: 'nowrap' }}>{c.label}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows === null || loading ? (
                        <tr><td colSpan={cols.length} style={{ padding: 40, textAlign: 'center' }}><div className="hrms-loader" style={{ justifyContent: 'center' }}><span /><span /><span /></div></td></tr>
                    ) : rows.length === 0 ? (
                        <tr><td colSpan={cols.length} style={{ padding: 40, textAlign: 'center', color: ui.muted }}>{empty ?? 'Nema podataka.'}</td></tr>
                    ) : (
                        rows.map((row) => (
                            <tr key={rowKey ? rowKey(row) : row.id} className="ui-row" onClick={onRowClick ? () => onRowClick(row) : undefined} style={{ cursor: onRowClick ? 'pointer' : 'default' }}>
                                {cols.map((c) => (
                                    <td key={c.key} style={{ padding: '12px 14px', textAlign: c.align ?? 'left', borderBottom: `1px solid ${ui.line2}`, color: ui.ink, verticalAlign: 'middle' }}>
                                        {c.render ? c.render(row) : (row as Record<string, unknown>)[c.key] as React.ReactNode}
                                    </td>
                                ))}
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    );
}

/* ── Avatar (inicijali) ──────────────────────────────────────────────────── */
export function Avatar({ name, size = 34 }: { name: string; size?: number }) {
    const init = (name || '?').split(' ').filter(Boolean).slice(0, 2).map((s) => s[0]).join('').toUpperCase() || '?';
    const hue = Array.from(name).reduce((a, c) => a + c.charCodeAt(0), 0) % 360;
    return <span style={{ width: size, height: size, minWidth: size, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.4, fontWeight: 700, color: '#fff', background: `linear-gradient(135deg, hsl(${hue},55%,58%), hsl(${(hue + 40) % 360},55%,46%))` }}>{init}</span>;
}

/* ── Breadcrumb ──────────────────────────────────────────────────────────── */
export function Crumbs({ items }: { items: string[] }) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: '0.8rem', color: ui.muted }}>
            {items.map((it, i) => (
                <React.Fragment key={i}>
                    {i > 0 ? <span style={{ color: '#c7cad6' }}>›</span> : null}
                    <span style={{ color: i === items.length - 1 ? ui.ink : ui.muted, fontWeight: i === items.length - 1 ? 700 : 500 }}>{it}</span>
                </React.Fragment>
            ))}
        </div>
    );
}

/* ── Tabs (kit) ──────────────────────────────────────────────────────────── */
export function Tabs<K extends string>({ tabs, active, onChange, size = 'md' }: { tabs: Array<{ key: K; label: string; icon?: string }>; active: K; onChange: (k: K) => void; size?: 'sm' | 'md' }) {
    return (
        <div style={{ display: 'inline-flex', gap: 4, background: '#eef0f6', padding: 4, borderRadius: 12, flexWrap: 'wrap' }}>
            {tabs.map((tb) => {
                const on = tb.key === active;
                return (
                    <button key={tb.key} type="button" onClick={() => onChange(tb.key)}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: size === 'sm' ? '6px 12px' : '8px 15px', borderRadius: 9, border: 'none', cursor: 'pointer', fontSize: size === 'sm' ? '0.8rem' : '0.86rem', fontWeight: 650, whiteSpace: 'nowrap', background: on ? '#fff' : 'transparent', color: on ? ui.acc : ui.ink2, boxShadow: on ? '0 1px 4px rgba(20,22,50,0.10)' : 'none' }}>
                        {tb.icon ? <span>{tb.icon}</span> : null}{tb.label}
                    </button>
                );
            })}
        </div>
    );
}

/* ── StatCard ────────────────────────────────────────────────────────────── */
export function StatCard({ icon, color, value, label, foot, sub, subColor, onClick }: { icon: React.ReactNode; color: string; value: React.ReactNode; label: string; foot?: React.ReactNode; sub?: string; subColor?: string; onClick?: () => void }) {
    return (
        <div onClick={onClick} style={{ background: ui.card, border: `1px solid ${ui.line}`, borderRadius: ui.radius, boxShadow: '0 1px 3px rgba(20,22,50,0.04), 0 8px 24px rgba(20,22,50,0.04)', padding: 18, display: 'flex', flexDirection: 'column', gap: 12, cursor: onClick ? 'pointer' : 'default', minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ width: 48, height: 48, borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem', color: '#fff', background: color, boxShadow: `0 6px 16px ${color}66` }}>{icon}</div>
                {sub ? <span style={{ fontSize: '0.74rem', fontWeight: 800, color: subColor ?? '#16a34a', background: (subColor ?? '#16a34a') + '1a', padding: '4px 9px', borderRadius: 8 }}>{sub}</span> : null}
            </div>
            <div>
                <div style={{ fontSize: '1.7rem', fontWeight: 800, color: ui.ink, lineHeight: 1.05 }}>{typeof value === 'number' ? <CountUp to={value} /> : value}</div>
                <div style={{ fontSize: '0.82rem', color: ui.muted, fontWeight: 600, marginTop: 3 }}>{label}</div>
            </div>
            {foot ? <div style={{ fontSize: '0.78rem', color: ui.acc, fontWeight: 650 }}>{foot}</div> : null}
        </div>
    );
}

/* ── BarRows (horizontalni bar chart) ────────────────────────────────────── */
export function BarRows({ rows }: { rows: Array<{ label: string; value: number; color?: string }> }) {
    const max = Math.max(1, ...rows.map((r) => r.value));
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
            {rows.map((r, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '110px 1fr 42px', alignItems: 'center', gap: 10 }}>
                    <div style={{ fontSize: '0.8rem', color: ui.ink2, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.label}</div>
                    <div style={{ height: 9, borderRadius: 999, background: '#eef0f6', overflow: 'hidden' }}>
                        <div style={{ width: `${(r.value / max) * 100}%`, height: '100%', borderRadius: 999, background: r.color ?? ui.acc, transition: 'width 0.4s ease' }} />
                    </div>
                    <div style={{ fontSize: '0.82rem', fontWeight: 700, color: ui.ink, textAlign: 'right' }}>{r.value}</div>
                </div>
            ))}
        </div>
    );
}

/* ── Donut chart ─────────────────────────────────────────────────────────── */
export function Donut({ segments, size = 150, centerLabel, centerValue }: { segments: Array<{ label: string; value: number; color: string }>; size?: number; centerLabel?: string; centerValue?: React.ReactNode }) {
    const total = Math.max(1, segments.reduce((a, s) => a + s.value, 0));
    const r = size / 2 - 14; const c = 2 * Math.PI * r; let off = 0;
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
            <div style={{ position: 'relative', width: size, height: size, flex: 'none' }}>
                <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
                    <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#eef0f6" strokeWidth={14} />
                    {segments.map((s, i) => {
                        const len = (s.value / total) * c; const el = <circle key={i} cx={size / 2} cy={size / 2} r={r} fill="none" stroke={s.color} strokeWidth={14} strokeDasharray={`${len} ${c - len}`} strokeDashoffset={-off} strokeLinecap="round" />; off += len; return el;
                    })}
                </svg>
                <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ fontSize: '1.5rem', fontWeight: 800, color: ui.ink }}>{centerValue ?? total}</div>
                    {centerLabel ? <div style={{ fontSize: '0.72rem', color: ui.muted }}>{centerLabel}</div> : null}
                </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                {segments.map((s, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.82rem' }}>
                        <span style={{ width: 10, height: 10, borderRadius: 3, background: s.color }} />
                        <span style={{ color: ui.ink2, fontWeight: 600 }}>{s.label}</span>
                        <span style={{ color: ui.ink, fontWeight: 700, marginLeft: 'auto' }}>{s.value}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

/* ── Segmentirana traka (Employee-Status stil) ───────────────────────────── */
export function SegBar({ segments }: { segments: Array<{ label: string; value: number; color: string }> }) {
    const total = Math.max(1, segments.reduce((a, s) => a + s.value, 0));
    return (
        <div>
            <div style={{ display: 'flex', height: 12, borderRadius: 999, overflow: 'hidden', gap: 2, background: '#eef0f6' }}>
                {segments.map((s, i) => <div key={i} style={{ flex: s.value || 0.0001, background: s.color, transition: 'flex 0.5s ease' }} title={`${s.label}: ${s.value}`} />)}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 24px', marginTop: 18 }}>
                {segments.map((s, i) => (
                    <div key={i}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: '0.8rem', color: ui.ink2, fontWeight: 600 }}><span style={{ width: 9, height: 9, borderRadius: 3, background: s.color }} />{s.label} <span style={{ color: ui.muted, fontWeight: 500 }}>{Math.round(100 * s.value / total)}%</span></div>
                        <div style={{ fontSize: '1.55rem', fontWeight: 800, color: ui.ink, marginTop: 2 }}>{s.value}</div>
                    </div>
                ))}
            </div>
        </div>
    );
}

/* ── CountUp — animirani brojač (premium tick 0 → n) ─────────────────────── */
export function CountUp({ to, dur = 950 }: { to: number; dur?: number }) {
    const [n, setN] = React.useState(0);
    React.useEffect(() => {
        if (!isFinite(to)) { setN(to); return; }
        let raf = 0; const t0 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
        const tick = (now: number) => {
            const p = Math.min(1, (now - t0) / dur);
            setN(Math.round((1 - Math.pow(1 - p, 3)) * to));
            if (p < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [to, dur]);
    return <>{n.toLocaleString('hr-HR')}</>;
}
